/**
 * The columns a project form reads and writes, and nothing that renders.
 *
 * Both directions live here together - record to fields, fields to request body
 * - because they describe ONE list of columns. Split across the two dialogs
 * they would be two lists, and the first column added to one of them would be
 * missing from the other; that is exactly how the create dialog ended up
 * without the start date, the description and the tags the route had accepted
 * all along.
 *
 * React-free on purpose (AGENTS.md §4): the interesting rule in this section is
 * which keys a save is allowed to carry, and it should be readable and testable
 * without mounting a dialog. `ProjectForm.tsx` re-exports everything public
 * here, so a caller can import from either.
 */
import type { BillingType, Project } from "../../../lib/projects";
import { toIso } from "./lib";

/**
 * The route caps `description` at 10000 characters and answers a failed parse
 * with a bare `invalid_body` that names no field. Enforced on the field too,
 * where the ceiling can stop a keystroke instead of turning a long paragraph
 * into a dialog that refuses to save and will not say why.
 */
export const DESCRIPTION_MAX = 10_000;

/** The same ceilings `createBody` states: thirty tags, fifty characters each. */
export const TAG_MAX_LEN = 50;
export const TAGS_MAX = 30;

/**
 * The delivery record and the links, as three groups of columns that behave
 * identically on the way in and out.
 *
 * Listed rather than written out ten times in each of the four conversions
 * below. The lists are what the loops walk, so a name added here without a
 * matching key on `ProjectFormValues` fails to compile - which is the direction
 * that matters, because the failure mode this section already suffered was a
 * column the route accepted and no form ever sent.
 *
 * `servicesRaw` is deliberately absent from all three. It is the untouched
 * Perfex text and the only evidence of what the old system said; a form that
 * let an operator retype it would destroy the one thing that makes a bad
 * normalisation reviewable afterwards.
 */
export const DELIVERY_TEXT = ["platform", "logoStatus", "pages"] as const;
export const LINK_TEXT = ["adminUrl", "hostingUrl", "dbAdminUrl", "cpanelUrl", "templateUrl"] as const;
export const DELIVERY_LISTS = ["services", "features"] as const;

export type DeliveryTextField = (typeof DELIVERY_TEXT)[number] | (typeof LINK_TEXT)[number];
export type DeliveryListField = (typeof DELIVERY_LISTS)[number];

/** The ceilings `shortText`, `linkText` and `textList` state on the route. A
 *  link gets far more room than a fact because a panel URL carries a path, a
 *  port and sometimes a session query. */
export const DELIVERY_TEXT_MAX = 120;
export const LINK_TEXT_MAX = 2000;
export const DELIVERY_LIST_ENTRY_MAX = 200;
export const DELIVERY_LIST_MAX = 50;

export interface ProjectFormValues {
  /** Null until a name is picked from the list: a typed name that was never
   *  picked is not a client. Edit mode never changes it. */
  clientId: string | null;
  clientName: string;
  name: string;
  description: string;
  /**
   * "" means the record does not say, which is what an imported project looks
   * like - `billingType` is nullable in the schema. `patchBody` cannot write it
   * back to null, so the blank is offered as a reading of the record and never
   * as a choice.
   */
  billingType: BillingType | "";
  /** Major units exactly as typed, not cents: the field is what the operator
   *  sees, and the conversion happens once, on the way out. */
  budget: string;
  hourlyRate: string;
  /**
   * The hours the project was sold on, exactly as typed. A string like the two
   * figures above it, for the same reason: the box holds what the operator
   * wrote, and the conversion happens once, on the way out.
   *
   * "" is the state every project in the register is in today - the column was
   * added with this field, and the Perfex import never carried an estimate - so
   * the blank has to survive a save as a blank rather than arrive as a zero
   * somebody agreed to.
   */
  estimatedHours: string;
  /** `YYYY-MM-DD`, the shape `DatePicker` reads and writes. */
  startDate: string;
  deadline: string;
  tags: string[];
  /**
   * Who is on this project, as platform user ids.
   *
   * Ids and not names: the directory that turns one into a face is loaded once
   * per session by `useStaff`, and a form holding names would have to keep them
   * in step with a list it does not own.
   *
   * Never reconciled against that directory, in either direction. An id it
   * cannot name - somebody taken off the host site - has to survive a save, or
   * the first operator to fix a typo in the project name silently drops them
   * from the project as a side effect. `UserPicker` draws such an id with
   * `UNKNOWN_MARK` and keeps its remove control, so leaving is a decision
   * somebody makes rather than something a load order does.
   *
   * `createBody` caps the list at fifty ids. Not enforced here, because it
   * cannot be reached: the picker can only offer the people the staff directory
   * holds, and the host site has a staff-sized membership list.
   */
  members: string[];
  /**
   * The delivery record and the links. Written out one by one rather than as a
   * mapped type over the lists above, because a reader of this interface should
   * be able to see what a project form holds without resolving two unions
   * first. The lists above are what the conversions walk; these are what makes
   * walking them typecheck.
   *
   * All ten are "" or [] rather than nullable. A form field has no null: the
   * operator either typed something or left the box empty, and turning an empty
   * box back into a cleared column is `projectPatch`'s decision, made once.
   */
  platform: string;
  logoStatus: string;
  pages: string;
  services: string[];
  features: string[];
  adminUrl: string;
  hostingUrl: string;
  dbAdminUrl: string;
  cpanelUrl: string;
  templateUrl: string;
}

/**
 * A typed figure in cents. Blank and nonsense both stay undefined rather than
 * becoming zero - see `money()` in `lib.ts`: zero is a budget somebody set,
 * absent is a decision nobody has made yet.
 */
export function centsOf(v: string): number | undefined {
  const n = Number.parseFloat(v.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : undefined;
}

function amountOf(cents: number | null): string {
  return cents == null ? "" : String(cents / 100);
}

/**
 * A typed estimate in hours. Blank and nonsense stay undefined, exactly like
 * `centsOf` - and on this column that distinction is the whole point: "nobody
 * estimated this" and "we estimated this at zero hours" are different claims,
 * and only one of them makes the first logged minute an overrun.
 *
 * No rounding, unlike `centsOf`. Cents has to land on a whole number because
 * `budgetCents` is an `Int`; `estimatedHours` is a `Float?` on the server, so
 * half an hour is representable and a value read out of the record types back
 * in unchanged - which is what keeps `projectPatch` below from posting a
 * "change" every time an untouched estimate passes through the dialog.
 */
export function hoursOf(v: string): number | undefined {
  const n = Number.parseFloat(v.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function hoursInput(hours: number | null): string {
  return hours == null ? "" : String(hours);
}

/** An ISO instant back into a date field's value. */
function dateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "";
}

/** Two lists holding the same entries in the same order. Used for the tags and
 *  for both delivery lists: order is meaningful on all three, because it is the
 *  order the chips were added in and the order they are drawn back. */
function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((entry, i) => entry === b[i]);
}

/** An empty form. Billing starts at fixed because that is the column default. */
export function blankProjectForm(): ProjectFormValues {
  return {
    clientId: null,
    clientName: "",
    name: "",
    description: "",
    billingType: "fixed",
    budget: "",
    hourlyRate: "",
    estimatedHours: "",
    startDate: "",
    deadline: "",
    tags: [],
    members: [],
    platform: "",
    logoStatus: "",
    pages: "",
    services: [],
    features: [],
    adminUrl: "",
    hostingUrl: "",
    dbAdminUrl: "",
    cpanelUrl: "",
    templateUrl: "",
  };
}

/** A stored project as this form's fields. */
export function projectToForm(p: Project): ProjectFormValues {
  return {
    clientId: p.clientId,
    clientName: p.client?.name ?? "",
    name: p.name,
    description: p.description ?? "",
    billingType: p.billingType ?? "",
    budget: amountOf(p.budgetCents),
    hourlyRate: amountOf(p.hourlyRateCents),
    estimatedHours: hoursInput(p.estimatedHours),
    startDate: dateInput(p.startDate),
    deadline: dateInput(p.deadline),
    tags: p.tags,
    // Copied whole, unfiltered. See the field's note: an id the staff directory
    // cannot name is still a membership, and dropping it here would turn every
    // unrelated edit into a quiet removal.
    members: p.members,
    platform: p.platform ?? "",
    logoStatus: p.logoStatus ?? "",
    pages: p.pages ?? "",
    services: p.services,
    features: p.features,
    adminUrl: p.adminUrl ?? "",
    hostingUrl: p.hostingUrl ?? "",
    dbAdminUrl: p.dbAdminUrl ?? "",
    cpanelUrl: p.cpanelUrl ?? "",
    templateUrl: p.templateUrl ?? "",
  };
}

/**
 * The POST body: only what was actually filled in.
 *
 * `createBody` takes every figure as optional, and leaving a blank field out is
 * how it stays blank instead of arriving as a zero the operator never typed.
 */
export function projectCreateBody(v: ProjectFormValues): Record<string, unknown> {
  const description = v.description.trim();
  const body: Record<string, unknown> = {
    clientId: v.clientId,
    name: v.name.trim(),
    description: description || undefined,
    billingType: v.billingType || undefined,
    budgetCents: centsOf(v.budget),
    hourlyRateCents: centsOf(v.hourlyRate),
    estimatedHours: hoursOf(v.estimatedHours),
    startDate: toIso(v.startDate),
    deadline: toIso(v.deadline),
    tags: v.tags.length ? v.tags : undefined,
    members: v.members.length ? v.members : undefined,
  };

  // Omitted rather than sent as null. `createBody` would accept the null - all
  // ten are nullable there - but a brand-new project has never had a value to
  // clear, and a body full of explicit nulls reads in a log as ten decisions
  // nobody made.
  for (const key of [...DELIVERY_TEXT, ...LINK_TEXT]) {
    const text = v[key].trim();
    if (text) body[key] = text;
  }
  for (const key of DELIVERY_LISTS) {
    if (v[key].length) body[key] = v[key];
  }
  return body;
}

/**
 * The PATCH body: the keys the operator actually changed, and nothing else.
 *
 * The omission is the feature. A project carries two columns no edit dialog
 * shows - `progressPct`, written by the overview slider, and `status`, written
 * by the row menu and the roadmap - and either can move while the dialog sits
 * open. A full-object PATCH would post what was true when it opened and quietly
 * undo whatever happened since.
 *
 * Not `changedFields` from `lib/clientPrefill.ts`, which sends an emptied field
 * as null. Here that would be a 400 on three of the columns: `patchBody` types
 * `description` as an optional string and both figures as optional non-negative
 * integers, none of them nullable. So a cleared description is sent as "", and
 * a cleared MONEY figure is not sent at all - the record keeps the number it
 * has, because this route has no way to say "no budget" and a zero would be an
 * agreement nobody made. `clearedFigure` lets the dialog admit that out loud
 * rather than dropping the edit in silence.
 *
 * `estimatedHours` is the exception among the figures, and not because clearing
 * hours is a different idea from clearing money: it is a different COLUMN. It
 * was added together with the field that writes it, so `patchBody` could take
 * it as nullable from the start, and an emptied box therefore sends null and
 * really does take the estimate back off. The two money figures predate that
 * and cannot be widened without deciding what an existing zero means.
 *
 * The two dates are the ones that CAN be cleared: `patchBody` widens both to
 * nullable, because the roadmap clears `startDate` when a card is dropped into
 * the "Future" column. So are the eight delivery and link texts, which is why
 * emptying one of those boxes sends null and actually clears the column instead
 * of quietly leaving the old value in place the way an emptied budget does.
 *
 * The two delivery LISTS, the tags and the members are sent whole or not at
 * all: a Prisma scalar list has no null and no partial update, and "no
 * services" - or nobody on the project - is the empty array rather than an
 * absence.
 */
export function projectPatch(p: Project, v: ProjectFormValues): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  const name = v.name.trim();
  if (name && name !== p.name) patch.name = name;

  const description = v.description.trim();
  if (description !== (p.description ?? "").trim()) patch.description = description;

  if (v.billingType && v.billingType !== p.billingType) patch.billingType = v.billingType;

  const budget = centsOf(v.budget);
  if (budget !== undefined && budget !== p.budgetCents) patch.budgetCents = budget;

  const rate = centsOf(v.hourlyRate);
  if (rate !== undefined && rate !== p.hourlyRateCents) patch.hourlyRateCents = rate;

  // Sent as null when emptied, unlike the two figures above. See this function's
  // header: the column is nullable on the route, so "there is no estimate" is
  // something this dialog can actually say. `?? undefined` on the stored side so
  // a record that has no estimate and a box that was left blank compare equal
  // and produce no key - null !== undefined would post an estimate-clearing
  // patch every time somebody opened the dialog to fix a typo.
  const hours = hoursOf(v.estimatedHours);
  if (hours !== (p.estimatedHours ?? undefined)) patch.estimatedHours = hours ?? null;

  if (v.startDate !== dateInput(p.startDate)) patch.startDate = toIso(v.startDate) ?? null;
  if (v.deadline !== dateInput(p.deadline)) patch.deadline = toIso(v.deadline) ?? null;

  if (!sameList(v.tags, p.tags)) patch.tags = v.tags;

  // Compared in order, like every other list here, though order carries no
  // meaning on this one - it is only the sequence the people were picked in. A
  // difference that is nothing but a reorder therefore rewrites the same set,
  // which is harmless; what matters is that an UNCHANGED list produces no key,
  // so opening the dialog to move a deadline cannot repost the membership.
  if (!sameList(v.members, p.members)) patch.members = v.members;

  for (const key of [...DELIVERY_TEXT, ...LINK_TEXT]) {
    const next = v[key].trim();
    if (next !== (p[key] ?? "").trim()) patch[key] = next || null;
  }
  for (const key of DELIVERY_LISTS) {
    if (!sameList(v[key], p[key])) patch[key] = v[key];
  }

  return patch;
}

/** True when a figure the record carries has been emptied on screen - the one
 *  edit `projectPatch` deliberately drops, and therefore the one it has to
 *  admit to. `estimatedHours` is deliberately not tested here: an emptied
 *  estimate IS sent, so warning about it would apologise for an edit that went
 *  through. */
export function clearedFigure(p: Project, v: ProjectFormValues): boolean {
  return (
    (p.budgetCents != null && centsOf(v.budget) === undefined) ||
    (p.hourlyRateCents != null && centsOf(v.hourlyRate) === undefined)
  );
}
